import { useQuery } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'

/**
 * What a deal adds over what its org already bills: new logo, cross-sell,
 * upsell or renewal, and each service line's increase or decrease.
 *
 * Asked of the server so the review page makes the same call the
 * #tf-newbusiness post makes when the deal is won, from the same function.
 */

export type NewBusinessKind = 'new_logo' | 'cross_sell' | 'upsell' | 'cross_sell_and_upsell' | 'renewal'
export type LineChange = 'new' | 'increase' | 'decrease' | 'same'

export interface DealLineChange {
  /** Heartbeat service line code, e.g. SEO, PPC, PAID_SOCIAL. */
  line: string
  before: number | null
  after: number
  delta: number
  change: LineChange
  products: string[]
}

export interface DealNewBusiness {
  kind: NewBusinessKind
  label: string
  isNewBusiness: boolean
  newMrr: number
  newServiceLines: string[]
  lines: DealLineChange[]
}

/**
 * `productSignature` changes whenever a product's line or price does, so an
 * edit on the Details tab refetches instead of showing the classification of
 * the deal as it was.
 */
export const useDealNewBusiness = (PK?: string, dealUuid?: string, productSignature = '') => {
  return useQuery({
    queryKey: ['dealNewBusiness', PK, dealUuid, productSignature],
    enabled: Boolean(PK && dealUuid),
    staleTime: 60 * 1000,
    retry: 1,
    queryFn: async (): Promise<DealNewBusiness> => {
      const { data } = await axios.get<DealNewBusiness>(
        `${baseUrl}/aws/deals/new-business/${encodeURIComponent(PK ?? '')}/${encodeURIComponent(dealUuid ?? '')}`
      )
      return data
    },
  })
}
